/**
 * Frontend service for communicating with our Express backend,
 * which proxies to the Mews Connector API.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

async function request(method, path, body = null) {
  const url = `${API_BASE}${path}`;
  const headers = { 'Content-Type': 'application/json' };

  // Attach auth token if available
  const token = localStorage.getItem('nordic_auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const options = { method, headers };
  if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);

  let data;
  try {
    data = await response.json();
  } catch {
    const error = new Error(`Request failed: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  if (!response.ok) {
    const error = new Error(data.error || `Request failed: ${response.status}`);
    error.status = response.status;
    error.detail = data.detail;
    throw error;
  }

  return data;
}

/**
 * Get enterprise configuration: room categories, rates, products, Nordic company ID.
 */
export async function getConfig() {
  return request('GET', '/api/mews/config');
}

/**
 * Search availability for given dates.
 * @param {string} startUtc - ISO 8601 check-in date
 * @param {string} endUtc - ISO 8601 check-out date
 * @param {string} [rateId] - Optional rate override
 */
export async function getAvailability(startUtc, endUtc, rateId) {
  return request('POST', '/api/mews/availability', { startUtc, endUtc, rateId });
}

/**
 * Find existing customer by email.
 * @param {string} email
 */
export async function findCustomer(email) {
  return request('POST', '/api/mews/customers/find', { email });
}

/**
 * Create a new customer (guest) in Mews.
 * @param {Object} customer
 */
export async function addCustomer(customer) {
  return request('POST', '/api/mews/customers/add', customer);
}

/**
 * Create reservations in Mews.
 * BookerId is set server-side from the authenticated booker — not sent here.
 * @param {Object} booking
 * @param {string} booking.groupName
 * @param {string} booking.poReference - Nordic PO####### number
 * @param {string} [booking.notes] - Free-text notes
 * @param {Array} booking.rooms - Room reservation lines
 */
export async function addReservations(booking) {
  return request('POST', '/api/mews/reservations/add', booking);
}

/**
 * Add a companion (additional guest) to an existing reservation.
 * @param {string} reservationId
 * @param {string} customerId
 */
export async function addCompanion(reservationId, customerId) {
  return request('POST', '/api/mews/reservations/addCompanion', { reservationId, customerId });
}
